import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { X, Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2, History, Sparkles } from "lucide-react";
import { api, API, formatApiError } from "../../lib/api";

/**
 * ImportSitesDialog — admin/manager tool to import sites from Excel.
 *
 * Two independent imports, selectable via tabs:
 *   • Historical / operational — existing live sites (created as operational).
 *   • New projects — pipeline sites (created at the default design stage).
 *
 * Flow per tab: download template → pick file (auto dry-run preview) →
 * review per-row actions (create / fill N blanks / skip / error) → Confirm.
 * Rows are matched to existing sites by Site Name + Postcode; by default only
 * blank fields are filled, with an admin "overwrite existing" option.
 */
const KINDS = [
  { key: "historical", label: "Historical / operational", icon: History,
    blurb: "Already-operational sites. Created as operational (Final Handover) so monitoring syncs. Provide a commissioning and/or completion date." },
  { key: "new", label: "New projects", icon: Sparkles,
    blurb: "Pipeline sites. Created at the default design stage ready to progress through the lifecycle." },
];

export default function ImportSitesDialog({ onClose, onImported }) {
  const [kind, setKind] = useState("historical");
  const [file, setFile] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const resetState = () => {
    setFile(null); setPreview(null); setCommitted(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const switchKind = (k) => {
    if (k === kind) return;
    setKind(k);
    resetState();
  };

  const runPreview = async (f, ow = overwrite) => {
    if (!f) return;
    setBusy(true); setCommitted(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", kind);
      fd.append("overwrite", ow ? "true" : "false");
      fd.append("dry_run", "true");
      const r = await api.post("/projects-import/site-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
      setPreview(null);
    } finally { setBusy(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(null); setCommitted(null);
    runPreview(f);
  };

  const toggleOverwrite = (checked) => {
    setOverwrite(checked);
    if (file) runPreview(file, checked);
  };

  const doCommit = async () => {
    if (!file || !preview) return;
    const actionable = (preview.will_create || 0) + (preview.will_update || 0);
    if (actionable === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      fd.append("overwrite", overwrite ? "true" : "false");
      fd.append("dry_run", "false");
      const r = await api.post("/projects-import/site-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCommitted(r.data);
      const parts = [];
      if (r.data.created) parts.push(`${r.data.created} created`);
      if (r.data.updated) parts.push(`${r.data.updated} updated`);
      if (r.data.skipped) parts.push(`${r.data.skipped} skipped`);
      toast.success(parts.length ? parts.join(" · ") : "Nothing to import");
      onImported && onImported(r.data.created + r.data.updated);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  const actionBadge = (r) => {
    if (r.errors?.length) {
      return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded"
                   style={{ background: "#FCE7E5", color: "#C0362C" }}
                   title={r.errors.join(" · ")} data-testid={`site-import-action-${r.row}`}>
        <AlertTriangle size={10} /> Error
      </span>;
    }
    if (r.action === "create")
      return <span className="inline-flex text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded"
                   style={{ background: "#DBEAFE", color: "#0E5BA8" }} data-testid={`site-import-action-${r.row}`}>Create</span>;
    if (r.action === "update")
      return <span className="inline-flex text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded"
                   style={{ background: "#FEF3C7", color: "#8A5A00" }}
                   title={`Will fill: ${r.fill_fields.join(", ")}`} data-testid={`site-import-action-${r.row}`}>
        Fill · {r.fill_count}
      </span>;
    return <span className="inline-flex text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded muted"
                 style={{ background: "#F1F1F1" }} title="Matched — nothing to change"
                 data-testid={`site-import-action-${r.row}`}>Skip</span>;
  };

  const actionable = preview ? (preview.will_create || 0) + (preview.will_update || 0) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="site-import-dialog">
      <div className="bg-white w-full max-w-5xl max-h-[88vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-[var(--line)]">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={16} />
            <div className="font-semibold">Import sites from Excel</div>
          </div>
          <button onClick={onClose} className="btn-ghost p-1" data-testid="site-import-close"><X size={14} /></button>
        </div>

        {/* Kind tabs */}
        <div className="flex border-b border-[var(--line)] bg-[var(--surface-2)]">
          {KINDS.map(k => {
            const Icon = k.icon;
            return (
              <button key={k.key} onClick={() => switchKind(k.key)}
                      data-testid={`site-import-kind-${k.key}`}
                      className={`flex items-center gap-2 px-4 py-3 text-xs font-mono uppercase tracking-widest ${kind === k.key ? "bg-white border-b-2 border-[var(--accent)] -mb-px" : "muted"}`}>
                <Icon size={13} /> {k.label}
              </button>
            );
          })}
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <p className="text-xs muted mb-4">{KINDS.find(k => k.key === kind)?.blurb}</p>

          {/* Step 1 — template */}
          <div className="mb-4 flex items-center justify-between gap-4 p-3 border border-[var(--line)]">
            <div>
              <div className="font-medium text-sm">1. Download the {kind === "historical" ? "historical" : "new-project"} template</div>
              <div className="text-xs muted">Excel workbook with every column, dropdowns, an example row and instructions.</div>
            </div>
            <a href={`${API}/projects-import/site-template.xlsx?kind=${kind}`}
               className="btn-primary inline-flex items-center gap-1.5"
               data-testid="site-import-download-template">
              <Download size={12} /> Template
            </a>
          </div>

          {/* Step 2 — upload */}
          <div className="mb-4 p-3 border border-[var(--line)]">
            <div className="font-medium text-sm mb-2">2. Upload your filled file</div>
            <input ref={inputRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden"
                   onChange={onFile} data-testid="site-import-file-input" />
            <div className="flex items-center gap-3 flex-wrap">
              <button className="btn-ghost inline-flex items-center gap-1.5" disabled={busy}
                      onClick={() => inputRef.current?.click()} data-testid="site-import-pick-file">
                <Upload size={12} /> {file ? "Choose different file…" : "Choose Excel or CSV"}
              </button>
              {file && <span className="text-sm muted truncate max-w-[200px]">{file.name}</span>}
              {busy && <Loader2 size={14} className="animate-spin" />}
              <label className="flex items-center gap-2 text-xs ml-auto cursor-pointer" title="Replace existing values too — not just blank fields">
                <input type="checkbox" checked={overwrite}
                       onChange={(e) => toggleOverwrite(e.target.checked)}
                       data-testid="site-import-overwrite" />
                Overwrite existing values (not just blanks)
              </label>
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div className="mb-2">
              <div className="flex items-center gap-4 text-sm mb-2 flex-wrap">
                <span data-testid="site-import-parsed">Parsed: <b>{preview.parsed}</b></span>
                <span className="text-[#0E5BA8]" data-testid="site-import-create">Create: <b>{preview.will_create}</b></span>
                <span className="text-[#8A5A00]" data-testid="site-import-update">Fill existing: <b>{preview.will_update}</b></span>
                <span className="muted" data-testid="site-import-skip">Skip: <b>{preview.will_skip}</b></span>
                {preview.errored > 0 && (
                  <span style={{ color: "#C0362C" }} data-testid="site-import-errored">
                    <AlertTriangle size={12} className="inline" /> Errors: <b>{preview.errored}</b>
                  </span>
                )}
              </div>
              <div className="border border-[var(--line)] max-h-[300px] overflow-y-auto">
                <table className="data-table text-xs" data-testid="site-import-preview-table">
                  <thead>
                    <tr><th>Row</th><th>Site</th><th>Customer</th><th>Postcode</th><th>kWp</th><th>Action</th><th>Detail</th></tr>
                  </thead>
                  <tbody>
                    {preview.rows.map(r => (
                      <tr key={r.row}
                          className={r.errors?.length ? "bg-[#FCE7E5]" : (r.action === "update" ? "bg-[#FFF7E6]" : "")}
                          data-testid={`site-import-row-${r.row}`} data-action={r.action}>
                        <td className="font-mono">{r.row}</td>
                        <td>{r.name || "—"}</td>
                        <td>{r.customer_name || "—"}</td>
                        <td className="font-mono text-[11px]">{r.postcode || "—"}</td>
                        <td>{r.system_size_kwp ?? "—"}</td>
                        <td>{actionBadge(r)}</td>
                        <td className="text-[11px] muted">
                          {r.errors?.length
                            ? r.errors.join(" · ")
                            : r.action === "update"
                              ? r.fill_fields.join(", ")
                              : r.action === "skip" ? "Matched existing — no new data" : "New site"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.errored > 0 && (
                <p className="text-xs muted mt-2">Rows with errors are skipped on import. Fix them in the sheet and re-upload.</p>
              )}
              {!overwrite && preview.will_update > 0 && (
                <p className="text-xs muted mt-2">Matched sites fill ONLY blank fields — existing values are never overwritten.</p>
              )}
            </div>
          )}

          {committed && (
            <div className="p-3 border border-[#17663C] bg-[#DBF3E4] text-sm mt-3" data-testid="site-import-commit-result">
              <CheckCircle2 size={12} className="inline mr-1 text-[#17663C]" />
              Created {committed.created} · Filled {committed.updated} · Skipped {committed.skipped}
              {committed.errored > 0 && ` (${committed.errored} skipped due to errors)`}.
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[var(--line)] flex items-center justify-between gap-2">
          <button className="btn-ghost" onClick={resetState} disabled={busy || !file} data-testid="site-import-reset">Start over</button>
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={onClose} data-testid="site-import-cancel">{committed ? "Done" : "Cancel"}</button>
            {!committed && (
              <button className="btn-primary inline-flex items-center gap-1.5" onClick={doCommit}
                      disabled={!preview || actionable === 0 || busy} data-testid="site-import-commit">
                <Upload size={12} />
                {preview ? `Import · ${preview.will_create} new / ${preview.will_update} fill` : "Import"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
